import { FC, useState, useRef, useCallback, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { profileActions } from '@/redux/actions';
import { userSelectors } from '@/redux/selectors';
import { Header, Sidebar, MenuBurger } from '@/components';
import { useOnClickOutside } from '@/hooks';
import useDevice from '@/hooks/useDevice';

import styles from './BaseLayout.module.sass';
import { RunOutChecks } from '@/pages/AmlCheckDetails/components/RunOutChecks/RunOutChecks';

interface BaseLayoutProps {}

const BaseLayout: FC<BaseLayoutProps> = () => {
  const location = useLocation();
  const { isMobile } = useDevice();
  const dispatch = useAppDispatch();
  const isAiTraceOnly = useAppSelector(userSelectors.checkIfAiTraceOnly);
  const isSidebarHidden = isAiTraceOnly && !isMobile;
  const sidebarRef = useRef<HTMLDivElement>(null);
  const menuBurgerRef = useRef<HTMLButtonElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  const [isActiveMenu, setIsActiveMenu] = useState<boolean>(false);
  const [isActiveTariffsPanel, setIsActiveTariffsPanel] = useState(false);

  const handleOnToggleMenu = useCallback(
    () => setIsActiveMenu(!isActiveMenu),
    [isActiveMenu],
  );

  const handleOnCloseMenu = useCallback(() => setIsActiveMenu(false), []);

  useOnClickOutside(
    [menuBurgerRef, sidebarRef, backdropRef],
    handleOnCloseMenu,
    undefined,
    [],
  );

  useEffect(() => {
    dispatch(profileActions.getProfileInfo());
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsActiveMenu(false);
    }
  }, [location.pathname, isMobile]);

  return (
    <div className={styles.layout}>
      {!isSidebarHidden && (
        <Sidebar
          ref={sidebarRef}
          backdropRef={backdropRef}
          isActive={isActiveMenu}
          onClose={handleOnCloseMenu}
        />
      )}
      <main
        className={cx(styles.main, {
          [styles['is-full-width']]: isSidebarHidden,
        })}
      >
        <Header
          isActiveTariffsPanel={isActiveTariffsPanel}
          onToggleTariffsPanel={setIsActiveTariffsPanel}
        >
          {!isSidebarHidden && (
            <MenuBurger
              ref={menuBurgerRef}
              isActive={isActiveMenu}
              onClick={handleOnToggleMenu}
            />
          )}
        </Header>
        <RunOutChecks onClick={() => setIsActiveTariffsPanel(true)} />
        <Outlet />
      </main>
    </div>
  );
};

export default BaseLayout;
